import React, { useState, useCallback, useMemo } from 'react'
import classNames from 'classnames'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'
import { RadioGroupContextProvider } from './context'
import PropTypes from 'prop-types'

const Group = React.forwardRef((props, ref) => {
    const {
        color,
        disabled,
        name,
        onChange,
        radioGutter,
        value: valueProp,
        vertical,
        className,
        field,
        form,
        ...rest
    } = props

    const [value, setValue] = useState(valueProp)

    const isInvalid = useMemo(() => {
        let validate = false
        if (!isEmpty(form) && field) {
            const touchedField = get(form.touched, field.name)
            const errorField = get(form.errors, field.name)
            validate = touchedField && errorField
        }
        return validate
    }, [form, field])

    const onRadioGroupChange = useCallback(
        (nextValue, e) => {
            setValue(nextValue)
            if (!isEmpty(form) && field) {
                form.setFieldTouched(field.name, true)
            }
            onChange?.(nextValue, e)
        },
        [onChange, setValue, form, field]
    )

    const contextValue = useMemo(
        () => ({
            vertical,
            name,
            value: typeof value === 'undefined' ? null : value,
            color,
            disabled,
            radioGutter,
            onChange: onRadioGroupChange,
        }),
        [
            disabled,
            onRadioGroupChange,
            vertical,
            name,
            color,
            radioGutter,
            value,
        ]
    )

    const radioGroupClass = classNames(
        'radio-group',
        vertical && 'vertical',
        isInvalid && 'ring-1 ring-red-500 rounded-md',
        className
    )

    const groupChild = () => {
        const { children, id } = props
        return (
            <div id={id} ref={ref} className={radioGroupClass} {...rest}>
                {children}
            </div>
        )
    }

    return (
        <RadioGroupContextProvider value={contextValue}>
            {groupChild()}
        </RadioGroupContextProvider>
    )
})

Group.defaultProps = {
    vertical: false,
    radioGutter: 3,
}

Group.propTypes = {
    vertical: PropTypes.bool,
    color: PropTypes.string,
    value: PropTypes.any,
    onChange: PropTypes.func,
    disabled: PropTypes.bool,
    name: PropTypes.string,
}

export default Group
